import React from "react";

// Type-specific SVG tank silhouettes for a richer, more distinctive product visual.
function TankSilhouette({ variant, accent }) {
  const stroke = accent;
  if (variant === "cylindrical") {
    return (
      <svg viewBox="0 0 200 200" fill="none" preserveAspectRatio="xMidYMid meet">
        <ellipse cx="100" cy="40" rx="60" ry="10" stroke={stroke} strokeWidth="1.5"/>
        <ellipse cx="100" cy="40" rx="60" ry="10" stroke={stroke} strokeWidth="0.5" strokeDasharray="2 2" transform="translate(0 4)"/>
        <path d="M40 40 L40 170 Q40 180 100 180 Q160 180 160 170 L160 40" stroke={stroke} strokeWidth="1.5"/>
        <line x1="40" y1="80" x2="160" y2="80" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
        <line x1="40" y1="120" x2="160" y2="120" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
        <line x1="40" y1="155" x2="160" y2="155" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
        <rect x="95" y="15" width="10" height="12" stroke={stroke} strokeWidth="1"/>
      </svg>
    );
  }
  if (variant === "sectional") {
    // rectangular sectional tank with panels grid
    return (
      <svg viewBox="0 0 200 200" fill="none" preserveAspectRatio="xMidYMid meet">
        <path d="M25 55 L100 25 L175 55 L175 165 L100 195 L25 165 Z" stroke={stroke} strokeWidth="1.5"/>
        <path d="M25 55 L100 85 L175 55" stroke={stroke} strokeWidth="1.5"/>
        <path d="M100 85 L100 195" stroke={stroke} strokeWidth="1.5"/>
        {/* panel grid on front face */}
        <line x1="25" y1="90" x2="100" y2="120" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        <line x1="25" y1="125" x2="100" y2="155" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        <line x1="50" y1="65" x2="50" y2="175" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        <line x1="75" y1="76" x2="75" y2="186" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        {/* right side */}
        <line x1="100" y1="120" x2="175" y2="90" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        <line x1="100" y1="155" x2="175" y2="125" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        <line x1="125" y1="76" x2="125" y2="186" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
        <line x1="150" y1="65" x2="150" y2="175" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 2"/>
      </svg>
    );
  }
  if (variant === "dome") {
    // Fire-water/utility styled tank with dome top
    return (
      <svg viewBox="0 0 200 200" fill="none" preserveAspectRatio="xMidYMid meet">
        <path d="M40 50 Q100 10 160 50" stroke={stroke} strokeWidth="1.5"/>
        <path d="M40 50 L40 175 L160 175 L160 50" stroke={stroke} strokeWidth="1.5"/>
        <line x1="40" y1="85" x2="160" y2="85" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
        <line x1="40" y1="120" x2="160" y2="120" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
        <line x1="40" y1="150" x2="160" y2="150" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
        <circle cx="100" cy="30" r="4" stroke={stroke} strokeWidth="1"/>
        <rect x="30" y="80" width="6" height="18" stroke={stroke} strokeWidth="0.8"/>
      </svg>
    );
  }
  // default = simple industrial silo
  return (
    <svg viewBox="0 0 200 200" fill="none" preserveAspectRatio="xMidYMid meet">
      <ellipse cx="100" cy="35" rx="55" ry="10" stroke={stroke} strokeWidth="1.5"/>
      <path d="M45 35 L45 170 L155 170 L155 35" stroke={stroke} strokeWidth="1.5"/>
      <line x1="45" y1="70" x2="155" y2="70" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
      <line x1="45" y1="110" x2="155" y2="110" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
      <line x1="45" y1="145" x2="155" y2="145" stroke={stroke} strokeWidth="0.6" strokeDasharray="2 3"/>
    </svg>
  );
}

function variantFromLabel(label = "") {
  const l = label.toLowerCase();
  if (l.includes("cylindrical") || l.includes("frp") || l.includes("potable")) return "cylindrical";
  if (l.includes("sectional") || l.includes("hdg") || l.includes("gls") || l.includes("gfs") || l.includes("zinc")) return "sectional";
  if (l.includes("fire") || l.includes("utility") || l.includes("water & utilities")) return "dome";
  return "sectional";
}

// Branded placeholder — technical, distinctive, PIPECO-branded.
export default function TankPlaceholder({ label = "PIPECO", subtitle = "Water Storage Solution", tone = "dark", code = "" }) {
  const bg = tone === "light" ? "#F5F3EE" : "#0F382C";
  const line = tone === "light" ? "rgba(15,56,44,0.15)" : "rgba(255,255,255,0.14)";
  const text = tone === "light" ? "#0F382C" : "#F5F3EE";
  const accent = tone === "light" ? "#1A5644" : "#5EEAD4";
  const variant = variantFromLabel(label + " " + subtitle);
  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: bg, color: text }}>
      {/* engineering grid */}
      <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none" viewBox="0 0 100 100">
        <defs>
          <pattern id="grid" width="5" height="5" patternUnits="userSpaceOnUse">
            <path d="M 5 0 L 0 0 0 5" fill="none" stroke={line} strokeWidth="0.15"/>
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#grid)"/>
      </svg>
      {/* Tank silhouette */}
      <div className="absolute right-[-15%] bottom-[-8%] w-[75%] opacity-30">
        <TankSilhouette variant={variant} accent={accent}/>
      </div>
      {/* dimension marks in top-right */}
      <div className="absolute top-4 right-4 font-mono text-[9px] tracking-widest uppercase" style={{ color: accent, opacity: 0.75 }}>
        <div>ø · h</div>
        <div className="mt-1">— · —</div>
      </div>
      <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.28em] uppercase" style={{ color: accent }}>
            <span className="inline-block w-6 h-[1px]" style={{ background: accent }}/>
            <span>PIPECO · GROUP</span>
          </div>
          {code && <div className="font-mono text-[10px] tracking-[0.25em] mt-2" style={{ color: text, opacity: 0.6 }}>{code}</div>}
          <div className="font-display text-xl md:text-3xl font-black uppercase leading-tight mt-3" style={{ color: text }}>{label}</div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase mt-2" style={{ color: text, opacity: 0.55 }}>{subtitle}</div>
        </div>
        <div className="w-12 h-[2px]" style={{ background: accent }}/>
      </div>
    </div>
  );
}
